import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../Api';

// Cores das notas (iguais em diários, boletins e legenda)
export const COR_ABAIXO = '#dc2626';
export const COR_MEDIA = '#2563eb';
export const COR_90 = '#16a34a';
export const COR_HIST = '#7A6F9B';

// Cor própria da recuperação (diferente das cores de nota: vermelho, azul, verde e histórico)
export const COR_REC = '#c026d3'; // fúcsia: não se confunde com vermelho/coral, azul, verde nem roxo-acinzentado (histórico)
// Cor da recuperação FINAL (anual): ciano, diferente de todas as outras
export const COR_RECF = '#0891b2';

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};
const fmt = (v, virgula = false) => {
    const n = num(v);
    if (n === null) return '-';
    return virgula ? n.toFixed(1).replace('.', ',') : n.toFixed(1);
};
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

// Mesma regra do servidor: nunca abaixo da nota do período
export function calcularFinal(original, rec, media, regra) {
    const o = num(original) ?? 0;
    const r = num(rec);
    if (r === null) return o;
    const m = num(media);
    let f = Math.max(o, r);
    if (regra === 'media') f = Math.max(o, (o + r) / 2);
    if (regra === 'ate_media') f = Math.max(o, m !== null ? Math.min(r, m) : r);
    return Math.round(f * 100) / 100;
}

/**
 * "10.0 Rec= 15.0" — a nota do período segue a cor normal da célula; "Rec=" mostra, na cor da
 * recuperação, a nota que vale pela regra da escola (ex.: até a média → 15, mesmo tirando 20).
 */
export function NotaComRec({ nota, rec, virgula = false }) {
    const r = num(rec);
    if (r === null) return <>{fmt(nota, virgula)}</>;
    return (
        <span className="nota-rec" style={{ whiteSpace: 'nowrap', fontSize: 'inherit' }} title="Nota do período e nota da recuperação">
            {fmt(nota, virgula)}{' '}
            <span style={{ color: COR_REC, fontWeight: 'inherit', fontSize: 'inherit' }}>Rec= {fmt(r, virgula)}</span>
        </span>
    );
}

/** "57.0 RecF= 60.0" — total do ano e, na cor da recuperação final, a nota que ficou pela regra da escola. */
export function NotaComRecF({ nota, recF, virgula = false }) {
    const r = num(recF);
    if (r === null) return <>{fmt(nota, virgula)}</>;
    return (
        <span className="nota-rec" style={{ whiteSpace: 'nowrap', fontSize: 'inherit' }} title="Total do ano e nota após a recuperação final">
            {fmt(nota, virgula)}{' '}
            <span style={{ color: COR_RECF, fontWeight: 'inherit', fontSize: 'inherit' }}>RecF= {fmt(r, virgula)}</span>
        </span>
    );
}

/** Para os itens do boletim da turma ({ total = nota que vale, totalOriginal, recuperacao }). */
export function NotaRec({ g }) {
    if (!g) return <>-</>;
    if (num(g.recuperacao) !== null) return <NotaComRec nota={g.totalOriginal ?? g.total} rec={g.total} />;
    return <>{fmt(g.total)}</>;
}

// Nota do período sem a recuperação (para a cor da célula)
export const notaDoPeriodo = (g) => {
    if (!g) return 0;
    const o = num(g.totalOriginal);
    return o !== null ? o : (num(g.total) ?? 0);
};

/**
 * Recuperações lançadas de uma turma (ou de um aluno numa turma).
 * recDe(periodoOuIdQuarter, id_student, materia) -> { notaRecuperacao, regra, ... } | null
 */
export function useRecuperacoes({ id_class, id_student, year } = {}) {
    const [lista, setLista] = useState([]);
    const [finais, setFinais] = useState([]);
    useEffect(() => {
        let ativo = true;
        if (!id_class && !id_student) return undefined;
        api.post('/recovery/list', { id_class, id_student, ...(year ? { year: String(year) } : {}) })
            .then((r) => { if (ativo) { setLista(r?.data?.data || []); setFinais(r?.data?.finais || []); } })
            .catch(() => { /* sem recuperações: mostra as notas normais */ });
        return () => { ativo = false; };
    }, [id_class, id_student, year]);

    return useMemo(() => {
        const recDe = (periodo, aluno, materia) => lista.find((r) =>
            (String(r.periodo) === String(periodo) || r.id_quarter === String(periodo)) &&
            (!aluno || r.id_student === String(aluno)) &&
            norm(r.materia) === norm(materia)
        ) || null;
        // nota que vale (para a cor da célula): a final pela regra da escola
        const finalDe = (periodo, aluno, materia, original, media) => {
            const r = recDe(periodo, aluno, materia);
            return r ? calcularFinal(original, r.notaRecuperacao, media, r.regra) : original;
        };
        // nota que vale pela regra da escola, ou null se não houver recuperação lançada
        const recFinal = (periodo, aluno, materia, original, media) => {
            const r = recDe(periodo, aluno, materia);
            return r ? calcularFinal(original, r.notaRecuperacao, media, r.regra) : null;
        };
        // recuperação FINAL: nota que ficou no ano (recalculada com o total do ano exibido), ou null
        const recFinalDe = (aluno, materia) => finais.find((f) =>
            (!aluno || f.id_student === String(aluno)) && norm(f.materia) === norm(materia)) || null;
        const recFinalAno = (aluno, materia, totalAno) => {
            const f = recFinalDe(aluno, materia);
            if (!f) return null;
            const t = num(totalAno) ?? num(f.totalAno) ?? 0;
            if (num(f.mediaAnual) !== null && t >= num(f.mediaAnual)) return null;
            return calcularFinal(t, f.recuperacao, f.mediaAnual, f.regra);
        };
        return { recDe, finalDe, recFinal, recFinalDe, recFinalAno, temAlguma: lista.length > 0 || finais.length > 0 };
    }, [lista, finais]);
}

// Regras de impressão da legenda e da nota com recuperação
const CSS_IMPRESSAO = `
.legenda-notas { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
@media print {
  .legenda-notas { font-size: 7.5pt !important; line-height: 1.2 !important; flex-wrap: nowrap !important;
    gap: 0 8px !important; padding: 1px 4px !important; margin: 2px 0 !important; overflow: visible !important; }
  .legenda-notas, .legenda-notas * { font-size: 7.5pt !important; white-space: nowrap !important; }
  .nota-rec, .nota-rec * { font-size: inherit !important; white-space: nowrap !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

/** Legenda compacta numa linha só (diário e boletins). As cores são texto (■), então saem na impressão. */
export function LegendaNotas({ total, media, totalLabel = 'Total', mediaLabel = 'Média', historico = true, recuperacao = true, recFinal = false }) {
    const item = (cor, txt) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
            <span aria-hidden style={{ color: cor, fontSize: '1.1em', lineHeight: 1 }}>■</span>{txt}
        </span>
    );
    return (
        <>
            <style>{CSS_IMPRESSAO}</style>
            <div className="legenda-notas" style={{
                display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '0 10px',
                overflowX: 'auto', maxWidth: '100%', boxSizing: 'border-box',
                fontSize: 11, lineHeight: 1.3, padding: '3px 8px', margin: '4px 0',
                border: '1px solid var(--line, #e3e8eb)', borderRadius: 6, background: 'var(--surface, #fff)',
            }}>
                {total !== undefined && <span style={{ whiteSpace: 'nowrap' }}>{totalLabel}: <strong style={{ color: COR_90 }}>{fmt(total)}</strong></span>}
                {media !== undefined && <span style={{ whiteSpace: 'nowrap' }}>{mediaLabel}: <strong style={{ color: COR_MEDIA }}>{fmt(media)}</strong></span>}
                {item(COR_ABAIXO, '< média')}
                {item(COR_MEDIA, '≥ média')}
                {item(COR_90, '≥ 90%')}
                {historico && item(COR_HIST, 'Histórico')}
                {recuperacao && (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        <strong style={{ color: COR_REC }}>Rec=</strong> recuperação
                    </span>
                )}
                {recFinal && (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        <strong style={{ color: COR_RECF }}>RecF=</strong> rec. final
                    </span>
                )}
            </div>
        </>
    );
}
